import { Fragment, Key, useEffect, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { JoveAutocomplete, JoveDateRangePicker, JoveNumberInput } from '@jove-frontend/mui';
import {
  Button,
  Checkbox,
  CheckboxControlled,
  CountryGetResponse,
  CountrySelectorControlled,
  Input,
  QuoteResponse,
  QuoteTagEnum,
  SelectInput,
  SelectOptionType,
  isPublishOnline,
  useAuthStore,
  useCountryStore,
  useLocalTranslation,
  usePopover,
} from '@jove-frontend/uikit';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Popover, Typography } from '@mui/material';
import { SubmitHandler, useForm } from 'react-hook-form';
import { fetchAnalyticsFilterOptions, transformAnalyticsFilterOptions } from '../../api/quoteApi';
import { TCatalogStore } from '../../store/catalog/CatalogStore';

type Props = {
  filterState: TCatalogStore<QuoteResponse>['filterState'];
  isOpen: boolean;
  handleFieldChange: (key: Key, value: unknown) => void;
  applyFilters: SubmitHandler<unknown>;
  clearFilters: () => void;
  onClose: () => void;
};

export const QuoteFilters = ({ filterState, isOpen, handleFieldChange, applyFilters, clearFilters, onClose }: Props) => {
  const [{ countries }, { initialize: initializeCountryStore }] = useCountryStore(({ state, actions }) => [state, actions]);
  const { control, register, handleSubmit, reset, watch } = useForm({
    defaultValues: filterState.filters,
    mode: 'onTouched',
  });
  const [assigneeSelectAll, setAssigneeSelectAll] = useState(false);
  const [statusSelectAll, setStatusSelectAll] = useState(false);
  const [utmSourceOptions, setUtmSourceOptions] = useState<SelectOptionType[]>([]);
  const [utmMediumOptions, setUtmMediumOptions] = useState<SelectOptionType[]>([]);
  const [utmCampaignOptions, setUtmCampaignOptions] = useState<SelectOptionType[]>([]);
  const [isLoadingUtmOptions, setIsLoadingUtmOptions] = useState(false);
  const { $t } = useLocalTranslation();
  const [selectOrganizationList, setSelectOrganizationList] = useState([]);
  const {
    state: { userRoles },
  } = useAuthStore();
  const { popoverOpen, popoverAnchorEl, openPopoverAction, closePopoverAction } = usePopover();
  const [partnersSelectAll, setPartnersSelectAll] = useState(false);

  /**
   * watch
   */
  const organizationsWatch = watch('organizations');

  /**
   * effects
   */
  useEffect(() => {
    updateOrganizationSelectList(organizationsWatch);
  }, [organizationsWatch]);

  useEffect(() => {
    initializeCountryStore();
    loadAnalyticsFilterOptions();
  }, []);

  const loadAnalyticsFilterOptions = async () => {
    try {
      setIsLoadingUtmOptions(true);
      const [data, error] = await fetchAnalyticsFilterOptions();

      if (error) {
        console.error('Error fetching analytics filter options:', error);
        return;
      }

      if (data) {
        // Transform API data into SelectOptionType arrays for each UTM parameter
        setUtmSourceOptions(transformAnalyticsFilterOptions(data.utm_source, $t('common:all_sources')));
        setUtmMediumOptions(transformAnalyticsFilterOptions(data.utm_medium, $t('common:all_mediums')));
        setUtmCampaignOptions(transformAnalyticsFilterOptions(data.utm_campaign, $t('common:all_campaigns')));
      }
    } catch (error) {
      console.error('Unexpected error fetching analytics filter options:', error);
    } finally {
      setIsLoadingUtmOptions(false);
    }
  };

  const handleCheckboxChange = (
    e,
    fieldName:
      | 'insuranceType'
      | 'status'
      | 'validationStatusList'
      | 'assignee'
      | 'purchaseProbability'
      | 'tags'
      | 'regions'
      | 'insurers'
      | 'partners'
      | 'quotingThrough'
      | 'removeDuplicatedList'
      | 'propertyAnalysisNotAvailable'
      | 'sanctionCheckReferral'
      | 'quoteCreationMethodList'
      | 'paymentStatus'
      | 'referralStatus'
      | 'referralAssigneeList'
  ) => {
    const checked = e.target.checked;
    const value = e.target.value;
    const field = [...filterState.filters[fieldName]];
    const index = field.findIndex((i) => i.value === value);

    field[index] = { ...field[index], checked: checked };
    handleFieldChange(fieldName, field);
  };

  const commonSelectAllAction = (fieldName, e) => {
    let checkedValue = e.target.checked;
    switch (fieldName) {
      case 'assignee':
        setAssigneeSelectAll(checkedValue);
        break;
      case 'status':
        setStatusSelectAll(checkedValue);
        break;
      case 'partners':
        setPartnersSelectAll(checkedValue);
        break;
      default:
        break;
    }
    toggleFilterAllData(fieldName, checkedValue);
  };

  const toggleFilterAllData = (fieldName, checked) => {
    const field = [...filterState.filters[fieldName]];
    for (let i = 0; i < field.length; i++) {
      field[i] = { ...field[i], checked };
    }
    handleFieldChange(fieldName, field);
  };

  const handleUtmSourceChange = (value) => {
    handleFieldChange('utm_source', value);
  };

  const handleUtmMediumChange = (value) => {
    handleFieldChange('utm_medium', value);
  };

  const handleUtmCampaignChange = (value) => {
    handleFieldChange('utm_campaign', value);
  };

  const organizationChangeAction = (list) => {
    const field = [...(filterState?.filters?.organizations || [])];
    for (let i = 0; i < field.length; i++) {
      field[i] = {
        ...field[i],
        checked: !!list?.find((item) => item.value === field[i].value),
      };
    }
    handleFieldChange('organizations', field);
    updateOrganizationSelectList(field);
  };

  const updateOrganizationSelectList = (list) => {
    const selectedList = (list || []).filter((item) => item.checked);
    setSelectOrganizationList(selectedList);
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog onClose={onClose}>
        <Dialog.Overlay className="fixed inset-0" />

        <Transition.Child
          as={Fragment}
          enter="ease-in duration-200"
          enterFrom="opacity-0 translate-x-full"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0 translate-x-full"
        >
          <div className="fixed right-0 top-0 flex h-full w-96 flex-col bg-white shadow-lg">
            <div className="flex h-max justify-between border-b border-black/10 px-4 py-3">
              <p className="p-large">{$t('pages:quotes.filters')}</p>
            </div>
            {filterState.isInitialized && (
              <div className="flex-grow overflow-y-auto overflow-x-hidden">
                <form onSubmit={handleSubmit(applyFilters)} className="flex flex-col space-y-7 px-4 py-3">
                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:search')}</p>
                    <Input
                      placeholder={$t('common:search')}
                      value={filterState.filters.search ?? ''}
                      {...register('search')}
                      onChange={(e) => handleFieldChange('search', e.target.value)}
                    />
                  </div>

                  <div>
                    <div className="mb-2 flex flex-row justify-between">
                      <p className="p-small font-semibold">{$t('common:assignee')}</p>
                      <Checkbox
                        id="assignee"
                        label={$t('common:select_all')}
                        value={assigneeSelectAll as any}
                        checked={assigneeSelectAll}
                        onChange={(e) => commonSelectAllAction('assignee', e)}
                      ></Checkbox>
                    </div>

                    <div className="max-h-36 overflow-auto">
                      {filterState.filters.assignee?.map((i, index) => (
                        <CheckboxControlled
                          control={control}
                          key={`${i.value}-${index}`}
                          value={i.value}
                          label={i.label}
                          name={`assignee[${i.label}].checked`}
                          checked={i.checked}
                          className="mb-3"
                          onChange={(e) => handleCheckboxChange(e, 'assignee')}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col space-y-2">
                    <p className="p-small font-semibold">{$t('page:quoteListTableColumnFilter.referral_assignee')}</p>
                    <div className="max-h-36 overflow-auto">
                      {filterState.filters.referralAssigneeList?.map((i, index) => (
                        <CheckboxControlled
                          control={control}
                          key={`${i.value}-${index}`}
                          value={i.value}
                          label={i.label}
                          name={`referralAssigneeList[${i.label}].checked`}
                          checked={i.checked}
                          className="mb-3"
                          onChange={(e) => handleCheckboxChange(e, 'referralAssigneeList')}
                        />
                      ))}
                    </div>
                  </div>

                  {!userRoles?.includes('ga-admin' as any) && (
                    <div>
                      <p className="p-small mb-2 font-semibold">{$t('common:insurer')}</p>

                      {filterState.filters.insurers?.map((i) => (
                        <CheckboxControlled
                          control={control}
                          key={i.value}
                          value={i.value}
                          label={i.label}
                          name={`insurers[${i.label}].checked`}
                          checked={i.checked}
                          className="mb-3"
                          onChange={(e) => handleCheckboxChange(e, 'insurers')}
                        />
                      ))}
                    </div>
                  )}

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:insurance_type')}</p>

                    {filterState.filters.insuranceType?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`insuranceType[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'insuranceType')}
                      />
                    ))}
                  </div>

                  <div>
                    <div className="mb-2 flex flex-row justify-between">
                      <p className="p-small mb-2 font-semibold">{$t('common:status')}</p>
                      <Checkbox
                        id="status"
                        label={$t('common:select_all')}
                        value={statusSelectAll as any}
                        checked={statusSelectAll}
                        onChange={(e) => commonSelectAllAction('status', e)}
                      ></Checkbox>
                    </div>

                    {filterState.filters.status?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`status[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'status')}
                      />
                    ))}
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('page:quoteListTableColumnFilter.validation_status')}</p>

                    {filterState.filters.validationStatusList?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`validationStatusList[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'validationStatusList')}
                      />
                    ))}
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:purchase_probability')}</p>

                    {filterState.filters.purchaseProbability?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`purchaseProbability[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'purchaseProbability')}
                      />
                    ))}
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:region')}</p>

                    {filterState.filters.regions?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`regions[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'regions')}
                      />
                    ))}
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:tags')}</p>

                    {filterState.filters.tags?.map((i) => (
                      <div className="mb-3 flex flex-row items-center space-x-1">
                        <CheckboxControlled
                          control={control}
                          key={i.value}
                          value={i.value}
                          label={i.label}
                          name={`tags[${i.label}].checked`}
                          checked={i.checked}
                          onChange={(e) => handleCheckboxChange(e, 'tags')}
                        />
                        {i.value === QuoteTagEnum().switchToJove.value && (
                          <InfoOutlined
                            className="text-brand-secondary h-4 w-4 cursor-pointer"
                            onMouseEnter={openPopoverAction}
                            onMouseLeave={closePopoverAction}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: 'none' }}
                    open={popoverOpen}
                    anchorEl={popoverAnchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onClose={closePopoverAction}
                    disableRestoreFocus
                  >
                    <Box className="max-w-[360px] p-2">
                      <Typography variant="subtitle2" className="font-medium">
                        {$t('page:quoteFilters.policy_expiry_action_required_60_days')}
                      </Typography>
                    </Box>
                  </Popover>

                  {!userRoles?.includes('ga-admin' as any) && (
                    <div>
                      <div className="mb-2 flex flex-row justify-between">
                        <p className="p-small font-semibold">{$t('pages:menus.partners')}</p>
                        <Checkbox
                          id="partners"
                          label={$t('common:select_all')}
                          value={partnersSelectAll as any}
                          checked={partnersSelectAll}
                          onChange={(e) => commonSelectAllAction('partners', e)}
                        ></Checkbox>
                      </div>

                      <div className="max-h-36 overflow-auto">
                        {filterState.filters.partners?.map((i, index) => (
                          <CheckboxControlled
                            control={control}
                            key={`${i.value}-${index}`}
                            value={i.value}
                            label={i.label}
                            name={`partners[${i.label}].checked`}
                            checked={i.checked}
                            className="mb-3"
                            onChange={(e) => handleCheckboxChange(e, 'partners')}
                          />
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('page:quoteListFilter.quote_source')}</p>

                    {filterState.filters.quotingThrough?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`quotingThrough[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'quotingThrough')}
                      />
                    ))}
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('page:quoteFilters.duplicated_quote')}</p>
                    {filterState.filters.removeDuplicatedList?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`removeDuplicatedList[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'removeDuplicatedList')}
                      />
                    ))}
                  </div>

                  {!isPublishOnline() && (
                    <>
                      <div>
                        <p className="p-small mb-2 font-semibold">Property analysis not available</p>
                        {filterState.filters.propertyAnalysisNotAvailable?.map((i) => (
                          <CheckboxControlled
                            control={control}
                            key={i.value}
                            value={i.value}
                            label={i.label}
                            name={`propertyAnalysisNotAvailable[${i.label}].checked`}
                            checked={i.checked}
                            className="mb-3"
                            onChange={(e) => handleCheckboxChange(e, 'propertyAnalysisNotAvailable')}
                          />
                        ))}
                      </div>

                      <div>
                        <p className="p-small mb-2 font-semibold">Sanction check referral</p>
                        {filterState.filters.sanctionCheckReferral?.map((i) => (
                          <CheckboxControlled
                            control={control}
                            key={i.value}
                            value={i.value}
                            label={i.label}
                            name={`sanctionCheckReferral[${i.label}].checked`}
                            checked={i.checked}
                            className="mb-3"
                            onChange={(e) => handleCheckboxChange(e, 'sanctionCheckReferral')}
                          />
                        ))}
                      </div>
                    </>
                  )}

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('page:quoteListTableColumnFilter.quote_creation_methods')}</p>
                    {filterState.filters.quoteCreationMethodList?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`quoteCreationMethodList[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'quoteCreationMethodList')}
                      />
                    ))}
                  </div>

                  {!isPublishOnline() && (
                    <div>
                      <p className="p-small mb-2 font-semibold">{$t('page:quoteListFilter.payment_status')}</p>
                      {filterState.filters.paymentStatus?.map((i) => (
                        <CheckboxControlled
                          control={control}
                          key={i.value}
                          value={i.value}
                          label={i.label}
                          name={`paymentStatus[${i.label}].checked`}
                          checked={i.checked}
                          className="mb-3"
                          onChange={(e) => handleCheckboxChange(e, 'paymentStatus')}
                        />
                      ))}
                    </div>
                  )}

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('page:quoteListFilter.referral_status')}</p>
                    {filterState.filters.referralStatus?.map((i) => (
                      <CheckboxControlled
                        control={control}
                        key={i.value}
                        value={i.value}
                        label={i.label}
                        name={`referralStatus[${i.label}].checked`}
                        checked={i.checked}
                        className="mb-3"
                        onChange={(e) => handleCheckboxChange(e, 'referralStatus')}
                      />
                    ))}
                    {!isPublishOnline() && (
                      <JoveDateRangePicker
                        value={filterState.filters.referralTimeRange}
                        valueChange={(value) => handleFieldChange('referralTimeRange', value)}
                      />
                    )}
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('page:quoteFilters.days_to_policy_document_expiry')}</p>
                    <JoveNumberInput
                      value={filterState.filters.minimumDaysPolicyToExpiry}
                      valueChange={(value) => handleFieldChange('minimumDaysPolicyToExpiry', value)}
                      size="small"
                      className="flex-1"
                      prefixText="<="
                    />
                  </div>

                  <div className="flex flex-col space-y-3">
                    <p className="p-small font-semibold">{$t('common:organization')}</p>
                    <JoveAutocomplete
                      multiple
                      options={filterState?.filters?.organizations || []}
                      value={selectOrganizationList}
                      valueChange={organizationChangeAction}
                      disableCloseOnSelect
                      label={$t('common:please_select')}
                    />
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:user_country')}</p>

                    <CountrySelectorControlled
                      control={control}
                      name="user_country"
                      multiple={false}
                      options={countries}
                      onUpdated={(country: CountryGetResponse) => handleFieldChange('user_country', country)}
                    />
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:business_country')}</p>

                    <CountrySelectorControlled
                      control={control}
                      name="business_country"
                      multiple={false}
                      options={countries}
                      onUpdated={(country: CountryGetResponse) => handleFieldChange('business_country', country)}
                    />
                  </div>

                  <div>
                    <p className="p-small mb-2 font-semibold">{$t('common:phone_country')}</p>

                    <CountrySelectorControlled
                      control={control}
                      name="phone_country"
                      multiple={false}
                      options={countries}
                      onUpdated={(country: CountryGetResponse) => handleFieldChange('phone_country', country)}
                    />
                  </div>

                  <div className="flex flex-col">
                    <p className="p-small mb-2 font-semibold">{$t('common:total_price')}</p>
                    <div className="flex flex-row items-center space-x-3">
                      <JoveNumberInput
                        value={filterState.filters.minimumPrice}
                        valueChange={(value) => handleFieldChange('minimumPrice', value)}
                        size="small"
                        placeholder={$t('common:minimum_price')}
                        className="flex-1"
                      />
                      <p> - </p>
                      <JoveNumberInput
                        value={filterState.filters.maximumPrice}
                        valueChange={(value) => handleFieldChange('maximumPrice', value)}
                        size="small"
                        placeholder={$t('common:maximum_price')}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col">
                    <p className="p-small mb-2 font-semibold">{$t('common:analytics')}</p>
                    <div className="mb-3">
                      <p className="p-small mb-1">{$t('common:source')}</p>
                      <SelectInput
                        value={
                          filterState.filters.utm_source
                            ? {
                                value: filterState.filters.utm_source,
                                label: filterState.filters.utm_source,
                              }
                            : { value: '', label: $t('common:all_sources') }
                        }
                        options={utmSourceOptions}
                        onChange={(value) => handleUtmSourceChange(value)}
                        disabled={isLoadingUtmOptions}
                      />
                    </div>
                    <div className="mb-3">
                      <p className="p-small mb-1">{$t('common:medium')}</p>
                      <SelectInput
                        value={
                          filterState.filters.utm_medium
                            ? {
                                value: filterState.filters.utm_medium,
                                label: filterState.filters.utm_medium,
                              }
                            : { value: '', label: $t('common:all_mediums') }
                        }
                        options={utmMediumOptions}
                        onChange={(value) => handleUtmMediumChange(value)}
                        disabled={isLoadingUtmOptions}
                      />
                    </div>
                    <div>
                      <p className="p-small mb-1">{$t('common:campaign')}</p>
                      <SelectInput
                        value={
                          filterState.filters.utm_campaign
                            ? {
                                value: filterState.filters.utm_campaign,
                                label: filterState.filters.utm_campaign,
                              }
                            : { value: '', label: $t('common:all_campaigns') }
                        }
                        options={utmCampaignOptions}
                        onChange={(value) => handleUtmCampaignChange(value)}
                        disabled={isLoadingUtmOptions}
                      />
                    </div>
                  </div>
                </form>
              </div>
            )}

            <div className="flex h-max justify-center space-x-2 border-t border-black/10 px-4 py-3">
              <Button
                color="secondary-outline"
                onClick={() => {
                  clearFilters();
                  reset(filterState.initialState);
                }}
              >
                <span className="px-6">{$t('common:clear_all')}</span>
              </Button>
              <Button color="primary" type="submit" onClick={handleSubmit(applyFilters)}>
                <span className="px-6">{$t('common:apply')}</span>
              </Button>
            </div>
          </div>
        </Transition.Child>
      </Dialog>
    </Transition>
  );
};
